import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { EnvironmentService } from '@nomoh-monorepo/shared-data-access-environment';
import {
  IClienteDependenteEntity,
  IEstabelecimentoClienteEntity,
} from '@nomoh-monorepo/shared-types';
import { firstValueFrom } from 'rxjs';
import { ClienteDataService } from './cliente.dataservice';
import { AgendaDataService } from '../feature-agenda/agenda.dataservice';
import { AgendaService } from '../feature-agenda/agenda.service';

/**
 * Service HTTP para Cliente do Estabelecimento
 */
@Injectable({
  providedIn: 'root',
})
export class ClienteService {
  private http = inject(HttpClient);
  private env = inject(EnvironmentService);
  private dataService = inject(ClienteDataService);
  private agendaDataService = inject(AgendaDataService);
  private agendaService = inject(AgendaService);

  private get baseUrl(): string {
    return `${this.env.getApiUrl('bardo')}/cliente`;
  }


  async findAllPaginado(
    estabelecimentoId: string,
    page = 1,
    limit = 10,
    filtro?: string
  ): Promise<{ data: IEstabelecimentoClienteEntity[]; total: number }> {
    try {
      this.dataService.setLoading(true);
      this.dataService.limparErro();
      
      const payload = { estabelecimentoId, page, limit, filtro };
      const response = await firstValueFrom(
        this.http.post<{ data: IEstabelecimentoClienteEntity[]; total: number }>(
          `${this.baseUrl}/findAllPaginado`,
          payload
        )
      );

      this.dataService.setClientes(response.data, response.total);
      return response;
    } catch (error) {
      const mensagem =
        (error as { error?: { message?: string } })?.error?.message ||
        'Erro ao buscar clientes';
      this.dataService.setErro(mensagem);
      throw error;
    } finally {
      this.dataService.setLoading(false);
    }
  }

  async criar(cliente: Partial<IEstabelecimentoClienteEntity>): Promise<string> {
    try {
      this.dataService.setLoading(true);
      this.dataService.limparErro();

      const response = await firstValueFrom<{ id: string }>(
        this.http.post<{ id: string }>(`${this.baseUrl}/create`, cliente)
      );

      return response.id;
    } catch (error) {
      const mensagem =
        (error as { error?: { message?: string } })?.error?.message ||
        'Erro ao criar cliente';
      this.dataService.setErro(mensagem);
      throw error;
    } finally {
      this.dataService.setLoading(false);
    }
  }

  async atualizar(
    cliente: Partial<IEstabelecimentoClienteEntity> & { id: string }
  ): Promise<void> {
    try {
      this.dataService.setLoading(true);
      this.dataService.limparErro();

      await firstValueFrom(this.http.put<void>(`${this.baseUrl}/update`, cliente));

      this.dataService.updateCliente(cliente.id, cliente);
      this.agendaDataService.atualizarClienteNosAgendamentos(cliente.id, cliente);
      this.agendaService
        .buscarAgenda()
        .catch((err) => console.error('Erro ao recarregar agenda', err));
    } catch (error) {
      const mensagem =
        (error as { error?: { message?: string } })?.error?.message ||
        'Erro ao atualizar cliente';
      this.dataService.setErro(mensagem);
      throw error;
    } finally {
      this.dataService.setLoading(false);
    }
  }

  async deletar(id: string): Promise<void> {
    try {
      this.dataService.setLoading(true);
      this.dataService.limparErro();

      await firstValueFrom(
        this.http.delete<void>(`${this.baseUrl}/delete`, {
          params: { id: id.toString() },
        })
      );

      this.dataService.removeCliente(id);
      this.agendaService
        .buscarAgenda()
        .catch((err) => console.error('Erro ao recarregar agenda', err));
    } catch (error) {
      const mensagem =
        (error as { error?: { message?: string } })?.error?.message ||
        'Erro ao deletar cliente. Verifique se ele não possui agendamentos.';
      this.dataService.setErro(mensagem);
      throw error;
    } finally {
      this.dataService.setLoading(false);
    }
  }

  async buscarMetricas(id: string): Promise<{
    cancelados: number;
    atendidos: number;
    confirmados: number;
    servicosMaisUtilizados: Array<{ nome: string; count: number }>;
  }> {
    try {
      this.dataService.setLoading(true);
      return await firstValueFrom(
        this.http.get<{
          cancelados: number;
          atendidos: number;
          confirmados: number;
          servicosMaisUtilizados: Array<{ nome: string; count: number }>;
        }>(`${this.baseUrl}/metricas`, { params: { id: id.toString() } })
      );
    } catch (error) {
      console.error('Erro ao buscar métricas do cliente', error);
      return { cancelados: 0, atendidos: 0, confirmados: 0, servicosMaisUtilizados: [] };
    } finally {
      this.dataService.setLoading(false);
    }
  }

  async findDependentes(
    responsavelId: string,
    estabelecimentoId: string
  ): Promise<IClienteDependenteEntity[]> {
    try {
      const res = await firstValueFrom(
        this.http.get<{ data?: IClienteDependenteEntity[] } | IClienteDependenteEntity[]>(
          `${this.baseUrl}/dependentes`,
          { params: { id: responsavelId, estabelecimentoId } }
        )
      );
      return Array.isArray(res) ? res : res?.data || [];
    } catch (error) {
      console.error('Erro ao buscar dependentes', error);
      return [];
    }
  }

  async findResponsaveis(
    clienteId: string,
    estabelecimentoId: string
  ): Promise<IClienteDependenteEntity[]> {
    try {
      const res = await firstValueFrom(
        this.http.get<{ data?: IClienteDependenteEntity[] } | IClienteDependenteEntity[]>(
          `${this.baseUrl}/responsaveis`,
          { params: { id: clienteId, estabelecimentoId } }
        )
      );
      return Array.isArray(res) ? res : res?.data || [];
    } catch (error) {
      console.error('Erro ao buscar responsáveis', error);
      return [];
    }
  }

  async criarVinculo(dto: {
    estabelecimentoId: string;
    clienteId: string;
    responsavelId: string;
    contatoPrincipal?: boolean;
  }): Promise<IClienteDependenteEntity> {
    try {
      this.dataService.setLoading(true);
      const res = await firstValueFrom(
        this.http.post<{ data?: IClienteDependenteEntity } | IClienteDependenteEntity>(
          `${this.baseUrl}/dependente`,
          dto
        )
      );
      const resultado = (res as { data?: IClienteDependenteEntity })?.data || (res as IClienteDependenteEntity);
      this.agendaService
        .buscarAgenda()
        .catch((err) => console.error('Erro ao recarregar agenda', err));
      return resultado;
    } catch (error) {
      const mensagem =
        (error as { error?: { message?: string } })?.error?.message ||
        'Erro ao criar vínculo de dependência';
      this.dataService.setErro(mensagem);
      throw error;
    } finally {
      this.dataService.setLoading(false);
    }
  }


  async removerVinculo(
    id: string,
    estabelecimentoId: string
  ): Promise<boolean> {
    try {
      this.dataService.setLoading(true);
      await firstValueFrom(
        this.http.delete<void>(`${this.baseUrl}/dependente`, {
          params: { id, estabelecimentoId },
        })
      );
      this.agendaService
        .buscarAgenda()
        .catch((err) => console.error('Erro ao recarregar agenda', err));
      return true;
    } catch (error) {
      const mensagem =
        (error as { error?: { message?: string } })?.error?.message ||
        'Erro ao remover vínculo de dependência';
      this.dataService.setErro(mensagem);
      throw error;
    } finally {
      this.dataService.setLoading(false);
    }
  }
}

